import { Button, Dialog, EmptyState, Icon, Markdown, Skeleton } from "@iec-id/ui";
import { useTranslation } from "react-i18next";
import { useLegalT } from "./i18n";
import { LegalDocumentNotFound, usePublicLegalDocument } from "./public-legal";

export interface LegalDocumentDialogProps {
	documentKey: string;
	/** Название на языке интерфейса — приходит от вызывающего (`useDocumentTitle`). */
	title: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/**
 * Текст документа модалкой поверх экрана, без ухода со страницы.
 *
 * Раньше согласия в мастере вели на `/legal/<key>` в новой вкладке. Формально
 * так текст доступен, фактически — человек уходил из мастера на середине шага
 * и возвращался не всегда. Информирование (GDPR ст. 12(1)) от этого не
 * страдало, а конверсия страдала.
 *
 * Запрос тот же публичный (`usePublicLegalDocument`), что и у читалки, и с тем
 * же кэшем react-query: открытая ранее вкладка `/legal/<key>` и эта модалка
 * показывают одну и ту же редакцию.
 *
 * Тексты только на английском (Д10) — подписи «нет на вашем языке» здесь нет
 * намеренно.
 */
export function LegalDocumentDialog({
	documentKey,
	title,
	open,
	onOpenChange,
}: LegalDocumentDialogProps) {
	const t = useLegalT();
	const { i18n } = useTranslation();
	// Запрос стартует только с открытием: три документа шага иначе тянулись бы
	// целиком при каждом показе экрана, а читают их единицы.
	const query = usePublicLegalDocument(documentKey, i18n.language, open);

	const notFound = query.error instanceof LegalDocumentNotFound;
	const document = query.data;

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title={title}
			{...(document
				? {
						description: t("reader.version", {
							version: document.version,
							date: new Date(document.effectiveFrom),
						}),
					}
				: {})}
			width={720}
			closeLabel={t("banner.close")}
			footer={
				<Button variant="second" onClick={() => onOpenChange(false)}>
					{t("reader.close")}
				</Button>
			}
		>
			{query.isPending && open ? (
				<div className="flex flex-col gap-3" role="status" aria-label={t("reader.loading")}>
					<Skeleton height={16} radius="chip" />
					<Skeleton height={220} radius="control" />
				</div>
			) : null}

			{query.isError ? (
				<EmptyState
					icon={<Icon name={notFound ? "CircleAlert" : "TriangleAlert"} size={32} />}
					title={notFound ? t("reader.notFoundTitle") : t("reader.errorTitle")}
					{...(notFound ? { description: t("reader.notFoundDescription") } : {})}
					{...(notFound
						? {}
						: {
								action: (
									<Button variant="second" onClick={() => void query.refetch()}>
										{t("reader.retry")}
									</Button>
								),
							})}
				/>
			) : null}

			{document ? (
				// Высота ограничена самой модалкой; длинный текст скроллится
				// внутри неё, а не растягивает окно за край экрана.
				<Markdown
					source={document.bodyMarkdown}
					className="iec-scroll max-h-[60vh] overflow-y-auto t-body text-fg-secondary"
				/>
			) : null}
		</Dialog>
	);
}
